import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { sendSmsToDriver } from './TripDashboard.service'
import type { SmsDriverFormValues, TripData } from './TripDashboard.types'

interface SmsDriverDialogPayload {
  trip: TripData
}

interface SmsDriverDialogProps {
  payload: SmsDriverDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const SmsDriverDialog = ({ payload, open, onAction, onClose }: SmsDriverDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<SmsDriverFormValues>()
  const sendSmsMutation = useMutation({
    mutationFn: (data: SmsDriverFormValues) => sendSmsToDriver(payload.trip.entityId, data),
    onError: (error) => setError('root', processServerErrorMessages(error)),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          phoneNumber: payload.trip.assignedDriver?.contactNumber,
          body: null,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<SmsDriverFormValues> = async (data: SmsDriverFormValues) => {
    await sendSmsMutation.mutateAsync(data)
    onAction()
    analyticsEvent('polestar_cs_sms_sent')
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={t('screens.cs.trip_dashboard.sms_driver.title')}
      maxWidth="xs"
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('form.send')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormText
          name="body"
          label={t('screens.cs.trip_dashboard.sms_driver.message')}
          control={control}
          rows={2}
          rules={{
            required: t('form.validation.required'),
          }}
        />
      </Stack>
    </KNDialog>
  )
}

export default SmsDriverDialog
